import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import { Activity, Users } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, } from "recharts";
import { Color } from "../../assets/color";
import RouteHeader from "../../components/common/RouteHeader";
import * as DashboardActions from '../../redux/actions/dashboardAction';
import moment from "moment/moment";

const Dashboard = () => {
  const fadeInDelays = [0, 100, 200, 300];
  const dispatch = useDispatch();
  const { dashboardData, recentActivityData } = useSelector(state => state?.dashboardReducer);

  useEffect(() => {
    //! Dispatching APIs
    dispatch(DashboardActions.getDashboard());
    dispatch(DashboardActions.getRecentActivity());
  }, []);

  return (
    <>
      <RouteHeader title={`Dashboard`} sub_title={`Overview of your system statistics and performance.`} />

      <Grid container spacing={3}>
        {Object?.keys(dashboardData)?.length > 0 && Object?.keys(dashboardData?.user)?.map((key, index) => (
          <Grid key={key} item xs={12} md={6} lg={3}>
            <div style={{ padding: "24px", borderRadius: "12px", boxShadow: "0 6px 16px rgba(0,0,0,0.1)", backgroundColor: "#fff", display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ width: "45px", height: "45px", borderRadius: "100px", background: "linear-gradient(to right,  #6653E8, #9544EB)", color: Color?.white, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Users size={20} />
                </div>
                <div style={{ fontSize: "14px", color: Color.grey, textTransform: 'capitalize' }}>Total {key}</div>
              </div>

              <div style={{ fontSize: "28px", fontWeight: 500 }}>{dashboardData?.user[key]?.total}</div>

              <div style={{ display: "flex", gap: "8px" }} >
                {dashboardData?.user[key]?.active !== undefined && <span style={{ backgroundColor: "#d1fae5", color: "#065f46", padding: "4px 8px", borderRadius: "999px", fontSize: "11.5px" }} >{dashboardData?.user[key]?.active} Active</span>}
                {dashboardData?.user[key]?.inactive !== undefined && <span style={{ backgroundColor: "#fee2e2", color: "#991b1b", padding: "4px 8px", borderRadius: "999px", fontSize: "11.5px" }}>{dashboardData?.user[key]?.inactive} Inactive</span>}
                {dashboardData?.user[key]?.increment_from_last_month !== undefined && <span style={{ backgroundColor: "#d1fae5", color: "#065f46", padding: "4px 8px", borderRadius: "999px", fontSize: "11.5px" }} >+{dashboardData?.user[key]?.increment_from_last_month} new this month</span>}
              </div>
            </div>
          </Grid>
        ))}

        <Grid item xs={12} md={6} lg={3}>
          <div style={{ padding: "24px", borderRadius: "12px", boxShadow: "0 6px 16px rgba(0,0,0,0.1)", backgroundColor: "#fff", display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div style={{ width: "45px", height: "45px", borderRadius: "100px", background: "linear-gradient(to right,  #6653E8, #9544EB)", color: Color?.white, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Activity size={20} />
              </div>
              <div style={{ fontSize: "14px", color: Color.grey }}>Reports Generated</div>
            </div>

            <div style={{ fontSize: "28px", fontWeight: 500 }}>{dashboardData?.reports_generated || 0}</div>

            <div style={{ display: "flex", gap: "8px" }} >
              <span style={{ backgroundColor: "#d1fae5", color: "#065f46", padding: "4px 8px", borderRadius: "999px", fontSize: "11.5px" }}>+{dashboardData?.reports_last_month || 0} new this month</span>
            </div>
          </div>
        </Grid>

        <Grid container item spacing={3} xs={12} md={12} lg={12}>
          <Grid item xs={12} lg={7} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, padding: "24px", borderRadius: "12px", boxShadow: "0 6px 16px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}>
              <div style={{ marginBottom: "16px" }}>
                <h2 style={{ fontSize: "20px", fontWeight: "bold", background: "linear-gradient(to right, #6653E8, #9544EB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", display: "inline-block", margin: 0, }}>Customer Age Distribution</h2>
                <p style={{ fontSize: "14px", color: "#6b7280", marginTop: "4px", }}>Breakdown of customers by age groups</p>
              </div>

              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashboardData?.customer_age_distribution} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.2} />
                    <XAxis dataKey="age" fontSize={12} />
                    <YAxis yAxisId="left" fontSize={12} />
                    <YAxis yAxisId="right" orientation="right" fontSize={12} unit="%" />
                    <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '20px' }} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />

                    <Bar yAxisId="left" dataKey="count" fill="url(#colorCount)" barSize={30} radius={[5, 5, 0, 0]} />
                    <Bar yAxisId="right" dataKey="percentage" fill="url(#colorPercentage)" barSize={30} radius={[5, 5, 0, 0]} />

                    <defs>
                      <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity={1} />
                        <stop offset="100%" stopColor="#4f46e5" stopOpacity={0.8} />
                      </linearGradient>
                      <linearGradient id="colorPercentage" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
                        <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.8} />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Grid>

          <Grid item xs={12} lg={5} style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, padding: "24px", borderRadius: "12px", boxShadow: "0 6px 16px rgba(0,0,0,0.1)", backgroundColor: "#fff" }}>
              <div style={{ marginBottom: "16px" }}>
                {/* display: "inline-block", // required to apply background to text */}
                <h2 style={{ fontSize: "20px", fontWeight: "bold", background: "linear-gradient(to right, #6653E8, #9544EB)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", display: "inline-block", margin: 0, }}>Recent Activity</h2>
                <p style={{ fontSize: "14px", color: "#6b7280", marginTop: "4px", }}>Latest system activities and events</p>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: '15px 0' }}>
                {recentActivityData && recentActivityData?.map((activity, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "16px", animation: "fadeIn 0.5s ease forwards", animationDelay: `${600 + i * 100}ms`, }}>
                    <div style={{ height: "40px", width: "40px", borderRadius: "9999px", background: "linear-gradient(to right,  #6653E8, #9544EB)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: "13.5px" }}>{activity?.message}</p>
                      <p style={{ fontSize: "13px", color: "#6b7280" }}>{moment(activity?.date)?.format('DD MMM YY @ hh:mm A')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default Dashboard;